import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { cn } from "@/lib/utils";

interface PlatformPreviewProps {
  variant: "pdv" | "finance";
}

const layerVariants: Variants = {
  rest: (custom: { z: number }) => ({
    z: 0,
    y: 0,
    x: 0,
    boxShadow: "0 0px 0px 0px hsl(var(--primary) / 0)",
    transition: { type: "spring", stiffness: 260, damping: 24 },
  }),
  hover: (custom: { z: number; x?: number; y?: number; delay?: number }) => ({
    z: custom.z,
    x: custom.x ?? 0,
    y: custom.y ?? -3,
    boxShadow:
      custom.z > 0
        ? `0 ${Math.round(custom.z * 0.6)}px ${Math.round(custom.z * 0.9)}px -12px hsl(var(--primary) / 0.35)`
        : "0 0px 0px 0px hsl(var(--primary) / 0)",
    transition: { type: "spring", stiffness: 260, damping: 22, delay: custom.delay ?? 0 },
  }),
};

const containerVariants: Variants = {
  rest: { scale: 1 },
  hover: { scale: 1.03, transition: { type: "spring", stiffness: 200, damping: 20 } },
};

/**
 * Mini-previews visuais das plataformas próprias em Exploded View:
 * no hover do card, as camadas do dashboard se separam no eixo Z
 * com sombras projetadas douradas (tokens da marca).
 */
const PlatformPreview = ({ variant }: PlatformPreviewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const reducedMotion = useReducedMotion();

  // O hover é capturado no card pai (group/platform) para a animação
  // responder em qualquer ponto do card do software.
  useEffect(() => {
    const el = ref.current;
    const card = el?.closest(".group\\/platform");
    if (!card) return;
    const enter = () => setHovered(true);
    const leave = () => setHovered(false);
    card.addEventListener("mouseenter", enter);
    card.addEventListener("mouseleave", leave);
    return () => {
      card.removeEventListener("mouseenter", enter);
      card.removeEventListener("mouseleave", leave);
    };
  }, []);

  const animate = hovered && !reducedMotion ? "hover" : "rest";

  return (
    <div style={{ perspective: 900 }} className="mt-4">
      <motion.div
        ref={ref}
        aria-hidden
        initial="rest"
        animate={animate}
        variants={containerVariants}
        style={{ transformStyle: "preserve-3d" }}
        className="pointer-events-none relative overflow-visible rounded-lg border border-border bg-secondary/60 p-3 shadow-inner"
      >
        {/* Barra de "janela" */}
        <div className="mb-3 flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-primary/40" />
          <span className="h-2 w-2 rounded-full bg-ring/60" />
          <span className="h-2 w-2 rounded-full bg-primary/20" />
          <span className="ml-2 h-1.5 w-16 rounded-full bg-muted-foreground/20" />
        </div>

        {variant === "pdv" ? (
          <div className="relative grid grid-cols-3 gap-2" style={{ transformStyle: "preserve-3d" }}>
            {/* Camada base: grid de produtos */}
            <motion.div
              variants={layerVariants}
              custom={{ z: 0 }}
              className="col-span-2 grid grid-cols-3 gap-1.5 rounded-md"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex h-9 items-end rounded-md p-1",
                    i % 3 === 0 ? "bg-primary/25" : i % 3 === 1 ? "bg-ring/30" : "bg-primary/10"
                  )}
                >
                  <span className="h-1 w-3/4 rounded-full bg-foreground/20" />
                </div>
              ))}
            </motion.div>

            {/* Camada 2: painel de pedidos/venda */}
            <motion.div
              variants={layerVariants}
              custom={{ z: 26, x: -4, delay: 0.04 }}
              className="flex flex-col gap-1.5 rounded-md bg-card/70 p-1.5"
            >
              <div className="h-3 rounded bg-muted-foreground/15" />
              <div className="h-3 w-2/3 rounded bg-muted-foreground/15" />
              <div className="mt-auto flex h-7 items-center justify-center rounded-md bg-primary">
                <span className="h-1.5 w-8 rounded-full bg-primary-foreground/70" />
              </div>
            </motion.div>

            {/* Camada 3: status do agente (flutuante) */}
            <motion.div
              variants={layerVariants}
              custom={{ z: 52, x: 6, y: -8, delay: 0.08 }}
              className="absolute -top-2 right-1 flex items-center gap-1.5 rounded-full border border-primary/30 bg-card px-2 py-1"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
              </span>
              <span className="h-1 w-10 rounded-full bg-foreground/25" />
            </motion.div>
          </div>
        ) : (
          <div className="relative flex items-end gap-2" style={{ transformStyle: "preserve-3d" }}>
            {/* Camada base: trilha do gráfico */}
            <div className="absolute inset-x-0 bottom-0 h-px bg-muted-foreground/15" />

            {/* Camada 2: gráfico de barras */}
            <motion.div
              variants={layerVariants}
              custom={{ z: 26, delay: 0.04 }}
              className="flex h-16 flex-1 items-end gap-1.5 rounded-md bg-card/60 p-1"
            >
              {[40, 65, 30, 80, 55, 95, 70].map((h, i) => (
                <div
                  key={i}
                  className={cn("flex-1 rounded-t-sm", i === 5 ? "bg-ring" : "bg-primary/40")}
                  style={{ height: `${h}%` }}
                />
              ))}
            </motion.div>

            {/* Camada 3: painel de indicadores */}
            <motion.div
              variants={layerVariants}
              custom={{ z: 48, x: -4, delay: 0.08 }}
              className="flex w-16 flex-col gap-1.5 rounded-md bg-card/80 p-1"
            >
              <div className="flex h-6 flex-col justify-center rounded bg-primary/20 px-1.5">
                <span className="h-1 w-full rounded-full bg-primary/50" />
                <span className="mt-1 h-1 w-2/3 rounded-full bg-primary/30" />
              </div>
              <div className="flex h-6 flex-col justify-center rounded bg-ring/25 px-1.5">
                <span className="h-1 w-full rounded-full bg-ring/60" />
                <span className="mt-1 h-1 w-2/3 rounded-full bg-ring/40" />
              </div>
            </motion.div>

            {/* Camada 4: status do agente (flutuante) */}
            <motion.div
              variants={layerVariants}
              custom={{ z: 64, x: 4, y: -8, delay: 0.12 }}
              className="absolute -top-2 left-1 flex items-center gap-1.5 rounded-full border border-primary/30 bg-card px-2 py-1"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
              </span>
              <span className="h-1 w-10 rounded-full bg-foreground/25" />
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PlatformPreview;
